import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Play, Plus, Trash2, Sparkles, X, ChevronDown, ChevronRight, Bot } from "lucide-react";

const TABS = [
  { key: "dashboard", label: "Overview" },
  { key: "prompts", label: "Prompt Bank" },
  { key: "runs", label: "Run History" },
];

const PROVIDER_LABELS = {
  openai: "GPT (OpenAI)",
  anthropic: "Claude (Anthropic)",
  gemini: "Gemini (Google)",
};
const PROVIDER_COLORS = {
  openai: "bg-emerald-600 text-white",
  anthropic: "bg-orange-500 text-white",
  gemini: "bg-blue-600 text-white",
};

/* ---------- Overview / Dashboard ---------- */
function OverviewTab() {
  const [analytics, setAnalytics] = useState({ runs: [] });
  const [running, setRunning] = useState(false);

  const load = () => api.get("/admin/aeo/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const triggerRun = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/aeo/runs", {});
      toast.success(`Run queued: ${r.data.id.slice(0, 8)} — refresh in a minute`);
      setTimeout(load, 3000);
    } catch { toast.error("Failed to start run"); }
    setRunning(false);
  };

  const latest = analytics.runs[analytics.runs.length - 1];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-inkmute max-w-xl">
          Every run tests every active prompt through GPT, Claude and Gemini, and measures how often Pollen Dots (and competitors) appear.
        </div>
        <button
          onClick={triggerRun}
          disabled={running}
          data-testid="aeo-run-now"
          className="inline-flex items-center gap-2 bg-forest text-cream text-xs uppercase tracking-widest px-4 py-2 rounded-full hover:bg-forestDeep disabled:opacity-50"
        >
          <Play className="w-4 h-4" /> {running ? "Queuing…" : "Run Now"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="p-6 rounded-xl bg-beige border border-forest/10">
          <div className="text-[10px] uppercase tracking-widest text-clay">Brand Mention Rate (latest)</div>
          <div className="font-serif text-5xl text-ink mt-1">
            {latest ? `${Math.round(latest.brand_mention_rate * 100)}%` : "—"}
          </div>
          <div className="text-xs text-inkmute mt-1">across every prompt × model</div>
        </div>
        <div className="p-6 rounded-xl bg-beige border border-forest/10">
          <div className="text-[10px] uppercase tracking-widest text-clay">Citations to your domain (latest)</div>
          <div className="font-serif text-5xl text-ink mt-1">{latest ? latest.citation_count : "—"}</div>
          <div className="text-xs text-inkmute mt-1">total URL citations across models</div>
        </div>
        <div className="p-6 rounded-xl bg-beige border border-forest/10">
          <div className="text-[10px] uppercase tracking-widest text-clay">Prompts tracked (latest)</div>
          <div className="font-serif text-5xl text-ink mt-1">{latest ? latest.prompt_count : "—"}</div>
          <div className="text-xs text-inkmute mt-1">active prompts in the last run</div>
        </div>
      </div>

      <div className="p-6 rounded-xl bg-beige border border-forest/10">
        <div className="text-sm font-medium text-ink mb-4">Share of AI voice over time</div>
        {analytics.runs.length === 0 ? (
          <div className="text-inkmute text-sm">No runs yet — press <b>Run Now</b> to gather your first AEO snapshot.</div>
        ) : (
          <div className="space-y-3">
            {analytics.runs.map((r) => (
              <div key={r.id} className="flex items-center gap-4">
                <div className="text-xs text-inkmute w-40 truncate">{new Date(r.started_at).toLocaleString()}</div>
                <div className="flex-1 flex items-center gap-1 h-6">
                  {["openai", "anthropic", "gemini"].map((p) => {
                    const per = (r.per_provider || {})[p];
                    const rate = per && per.count ? per.brand_mentions / per.count : 0;
                    return (
                      <div key={p} className={`h-full rounded ${PROVIDER_COLORS[p]}`} style={{ width: `${Math.max(4, rate * 100)}%`, opacity: rate === 0 ? 0.25 : 1 }} title={`${PROVIDER_LABELS[p]}: ${Math.round(rate * 100)}%`} />
                    );
                  })}
                </div>
                <div className="text-xs text-ink w-16 text-right font-serif">{Math.round(r.brand_mention_rate * 100)}%</div>
              </div>
            ))}
            <div className="flex gap-4 mt-4 text-[10px] uppercase tracking-widest text-inkmute">
              {["openai", "anthropic", "gemini"].map((p) => (
                <div key={p} className="flex items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded ${PROVIDER_COLORS[p]}`} /> {PROVIDER_LABELS[p]}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Prompts tab ---------- */
function PromptsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { text: "", intent: "informational", cluster: "general", priority: 3, active: true };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/admin/aeo/prompts").then((r) => setItems(r.data)).catch(() => toast.error("Load failed"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.text.trim()) return toast.error("Prompt text required");
    try {
      if (editing === "new") await api.post("/admin/aeo/prompts", form);
      else await api.patch(`/admin/aeo/prompts/${editing}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete prompt?")) return;
    await api.delete(`/admin/aeo/prompts/${id}`); load();
  };
  const toggle = async (p) => {
    await api.patch(`/admin/aeo/prompts/${p.id}`, { active: !p.active }); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-inkmute">Prompts are the questions we run every week. Aim for 30–100 realistic customer questions.</div>
        <button onClick={() => { setEditing("new"); setForm(empty); }} className="inline-flex items-center gap-2 bg-forest text-cream text-xs uppercase tracking-widest px-4 py-2 rounded-full hover:bg-forestDeep" data-testid="aeo-new-prompt">
          <Plus className="w-4 h-4" /> New Prompt
        </button>
      </div>

      {editing && (
        <div className="mb-6 rounded-xl border border-forest/15 bg-beige p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-serif text-xl text-ink">{editing === "new" ? "New Prompt" : "Edit Prompt"}</div>
            <button onClick={() => setEditing(null)} className="text-inkmute hover:text-forest"><X className="w-4 h-4" /></button>
          </div>
          <label className="block mb-3">
            <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Prompt</div>
            <textarea data-testid="aeo-prompt-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3}
              className="w-full bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm text-ink" />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Intent</div>
              <select value={form.intent} onChange={(e) => setForm({ ...form, intent: e.target.value })} className="w-full bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm">
                {["informational", "transactional", "local", "comparison", "navigational"].map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Cluster</div>
              <input value={form.cluster} onChange={(e) => setForm({ ...form, cluster: e.target.value })} className="w-full bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Priority (1–5)</div>
              <input type="number" min={1} max={5} value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} className="w-full bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm" />
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> <span className="text-sm">Active</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-widest px-4 py-2 border border-forest/20 rounded-full">Cancel</button>
            <button onClick={save} className="text-xs uppercase tracking-widest px-4 py-2 rounded-full bg-forest text-cream" data-testid="aeo-prompt-save">Save</button>
          </div>
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-forest/10 bg-beige">
        <table className="w-full text-sm">
          <thead className="bg-cream border-b border-forest/10 text-[10px] uppercase tracking-[0.2em] text-clay">
            <tr>
              <th className="text-left p-4">Prompt</th>
              <th className="text-left p-4 hidden md:table-cell">Cluster</th>
              <th className="text-left p-4 hidden lg:table-cell">Intent</th>
              <th className="text-left p-4">Priority</th>
              <th className="text-left p-4">Active</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-inkmute">No prompts yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-forest/10 hover:bg-cream/60">
                <td className="p-4 text-ink">{p.text}</td>
                <td className="p-4 hidden md:table-cell text-xs text-inkmute">{p.cluster}</td>
                <td className="p-4 hidden lg:table-cell text-xs text-inkmute">{p.intent}</td>
                <td className="p-4 text-xs">{p.priority}</td>
                <td className="p-4">
                  <button onClick={() => toggle(p)} className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${p.active ? "bg-forest text-cream" : "border border-forest/20 text-inkmute"}`}>{p.active ? "on" : "off"}</button>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => { setEditing(p.id); setForm({ text: p.text, intent: p.intent, cluster: p.cluster, priority: p.priority, active: p.active }); }} className="text-xs uppercase tracking-widest text-forest mr-4 link-underline">Edit</button>
                  <button onClick={() => remove(p.id)} className="text-inkmute hover:text-forest"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Runs tab ---------- */
function RunsTab() {
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(null);
  const [results, setResults] = useState([]);
  const [expandedResult, setExpandedResult] = useState(null);

  const load = () => api.get("/admin/aeo/runs").then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const openRun = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    const r = await api.get(`/admin/aeo/runs/${id}/results`);
    setResults(r.data);
  };

  return (
    <div>
      <div className="text-sm text-inkmute mb-4">Weekly scheduled runs on Sunday 03:00 UTC. Manual runs from the Overview tab.</div>

      <div className="space-y-3">
        {runs.length === 0 && <div className="text-inkmute text-sm">No runs yet. Trigger one from the Overview tab.</div>}
        {runs.map((r) => (
          <div key={r.id} className="rounded-xl border border-forest/10 bg-beige overflow-hidden">
            <button onClick={() => openRun(r.id)} className="w-full flex items-center justify-between p-4 hover:bg-cream text-left">
              <div className="min-w-0">
                <div className="text-ink font-medium">{new Date(r.started_at).toLocaleString()}</div>
                <div className="text-xs text-inkmute">
                  <span className={`inline-block text-[9px] px-2 py-0.5 rounded-full uppercase tracking-widest mr-2 ${
                    r.status === "done" ? "bg-forest text-cream" : r.status === "running" ? "bg-pollen text-ink" : r.status === "failed" ? "bg-red-500 text-white" : "bg-beige text-inkmute"
                  }`}>{r.status}</span>
                  {r.trigger} · {r.result_count} results · brand mention {Math.round((r.brand_mention_rate || 0) * 100)}% · {r.citation_count || 0} citations
                </div>
              </div>
              {open === r.id ? <ChevronDown className="w-4 h-4 text-inkmute" /> : <ChevronRight className="w-4 h-4 text-inkmute" />}
            </button>
            {open === r.id && (
              <div className="border-t border-forest/10 bg-cream max-h-[600px] overflow-auto">
                {results.length === 0 && <div className="p-4 text-inkmute text-sm">{r.status === "running" ? "Running — check back in a minute." : "No results."}</div>}
                {results.map((res) => (
                  <div key={res.id} className="border-b border-forest/10 p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="text-xs text-inkmute uppercase tracking-widest">{PROVIDER_LABELS[res.provider]} · {res.model_name}</div>
                        <div className="text-ink font-medium mt-1 text-sm">{res.prompt_text}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {res.brand_mentioned ? (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-forest text-cream">Mentioned ×{res.brand_mention_count}</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-beige text-inkmute">Not mentioned</span>
                        )}
                        {(res.urls_cited?.length || 0) > 0 && (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-pollen text-ink">{res.urls_cited.length} URLs</span>
                        )}
                      </div>
                    </div>
                    {(res.competitors_mentioned?.length || 0) > 0 && (
                      <div className="text-xs mt-2 text-inkmute">Competitors seen: <span className="text-ink">{res.competitors_mentioned.join(", ")}</span></div>
                    )}
                    <button onClick={() => setExpandedResult(expandedResult === res.id ? null : res.id)} className="mt-2 text-xs uppercase tracking-widest text-forest link-underline">
                      {expandedResult === res.id ? "Hide response" : "View AI response"}
                    </button>
                    {expandedResult === res.id && (
                      <div className="mt-3 rounded-lg bg-beige border border-forest/10 p-3 text-sm text-ink whitespace-pre-wrap max-h-72 overflow-auto">
                        {res.error ? <span className="text-red-600">Error: {res.error}</span> : (res.response_text || "(empty response)")}
                        {(res.urls_cited?.length || 0) > 0 && (
                          <div className="mt-3 pt-2 border-t border-forest/10">
                            <div className="text-[10px] uppercase tracking-widest text-clay mb-1">URLs cited</div>
                            {res.urls_cited.map((u, i) => <div key={i} className="text-xs text-forest break-all">{u}</div>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Main ---------- */
export default function AEODashboard() {
  const [tab, setTab] = useState("dashboard");
  useEffect(() => { document.title = "AEO · Pollen Dots CMS"; }, []);

  return (
    <Layout>
    <div data-testid="aeo-dashboard">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.24em] text-clay">CMS · AI Visibility (AEO)</div>
        <h1 className="font-serif text-5xl text-ink mt-1">Answer Engine Tracker</h1>
        <p className="text-sm text-inkmute mt-2 max-w-2xl">
          Track how ChatGPT, Claude and Gemini answer real customer questions — and how often they mention Pollen Dots.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 border-b border-forest/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`aeo-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors border-b-2 -mb-px ${
              tab === t.key ? "border-forest text-forest" : "border-transparent text-inkmute hover:text-ink"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && <OverviewTab />}
      {tab === "prompts" && <PromptsTab />}
      {tab === "runs" && <RunsTab />}
    </div>
    </Layout>
  );
}
